// src/api/contactsApi.ts

import { baseApi } from './baseApi';
import type {
  Contact,
  ContactsResponse,
  ContactContextResponse,
  CreateContactRequest,
  UpdateContactRequest,
  ListContactsParams,
} from '../types/api';
import { normalizeContactsListResponse } from '../utils/listNormalizers';

// Walk state.queries for cached entries of a given endpoint. Bypasses
// selectInvalidatedBy which only surfaces entries whose providesTags have
// been registered — the authoritative source of "what caches exist" is
// state.queries itself.
function collectCachedArgs(state: unknown, endpointName: string): unknown[] {
  const queries = (state as any)?.[baseApi.reducerPath]?.queries;
  if (!queries || typeof queries !== 'object') return [];
  const args: unknown[] = [];
  for (const key of Object.keys(queries)) {
    const entry = queries[key];
    if (entry && entry.endpointName === endpointName) {
      args.push(entry.originalArgs);
    }
  }
  return args;
}

export const contactsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getContacts: builder.query<ContactsResponse, ListContactsParams>({
      query: (params) => ({
        url: '/contacts',
        params: {
          ...(params.workspaceId && { workspaceId: params.workspaceId }),
          ...(params.companyId && { companyId: params.companyId }),
          ...(params.q && { q: params.q }),
          ...(params.type && { type: params.type }),
          ...(params.tags && { tags: params.tags }),
          ...(params.circleId && { circleId: params.circleId }),
          ...(params.limit && { limit: params.limit }),
          ...(params.offset && { offset: params.offset }),
        },
      }),
      transformResponse: (response: unknown) => normalizeContactsListResponse(response),
      providesTags: (result, error, params) =>
        result
          ? [
              ...result.contacts.map(({ id }) => ({ type: 'Contact' as const, id })),
              { type: 'Contact', id: params.workspaceId || 'LIST' },
            ]
          : [{ type: 'Contact', id: params.workspaceId || 'LIST' }],
    }),

    getContact: builder.query<Contact, string>({
      query: (id) => `/contacts/${id}`,
      providesTags: (result, error, id) => [{ type: 'Contact', id }],
    }),

    createContact: builder.mutation<Contact, CreateContactRequest>({
      query: (body) => ({
        url: '/contacts',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { workspaceId, companyId, companyIds }) => [
        { type: 'Contact', id: workspaceId || 'LIST' },
        // Linked companies gain a contact: their counts and people lists change.
        ...(companyId || (companyIds && companyIds.length > 0) ? (['Company'] as const) : []),
      ],
    }),

    updateContact: builder.mutation<Contact, { id: string } & UpdateContactRequest>({
      query: ({ id, ...body }) => ({
        url: `/contacts/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Contact', id },
        { type: 'Contact', id: 'LIST' },
      ],
      // Optimistic merge — the tag-invalidation refetch of the list can
      // be slow on large workspaces, so patch the cached row now and
      // roll back if the PATCH fails. Applies to every cached list
      // variant AND the single-contact detail cache used by the
      // slide-over. Walks state.queries directly instead of relying on
      // selectInvalidatedBy (which only picks up entries after their
      // providesTags have registered).
      onQueryStarted: async (arg, { dispatch, getState, queryFulfilled }) => {
        const patches: { undo: () => void }[] = [];
        const { id: _id, ...patch } = arg;
        for (const originalArgs of collectCachedArgs(getState(), 'getContacts')) {
          patches.push(
            dispatch(
              contactsApi.util.updateQueryData(
                'getContacts',
                originalArgs as any,
                (draft) => {
                  const row = draft.contacts.find((c) => c.id === arg.id);
                  if (!row) return;
                  for (const [k, v] of Object.entries(patch)) {
                    if (v === undefined) continue;
                    (row as any)[k] = v;
                  }
                },
              ),
            ),
          );
        }
        for (const originalArgs of collectCachedArgs(getState(), 'getContact')) {
          if (originalArgs !== arg.id) continue;
          patches.push(
            dispatch(
              contactsApi.util.updateQueryData(
                'getContact',
                originalArgs as any,
                (draft) => {
                  for (const [k, v] of Object.entries(patch)) {
                    if (v === undefined) continue;
                    (draft as any)[k] = v;
                  }
                },
              ),
            ),
          );
        }
        try {
          const { data: fresh } = await queryFulfilled;
          // Belt-and-suspenders — after the PATCH confirms, write the
          // authoritative row back over every cached list variant. This
          // guarantees the UI reflects the server's canonical state even
          // if the tag-invalidation refetch is slow or gets deduped.
          for (const originalArgs of collectCachedArgs(getState(), 'getContacts')) {
            dispatch(
              contactsApi.util.updateQueryData(
                'getContacts',
                originalArgs as any,
                (draft) => {
                  const idx = draft.contacts.findIndex((c) => c.id === arg.id);
                  if (idx === -1) return;
                  draft.contacts[idx] = { ...draft.contacts[idx], ...fresh };
                },
              ),
            );
          }
        } catch {
          patches.forEach((p) => p.undo());
        }
      },
    }),

    deleteContact: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/contacts/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Contact', id },
        { type: 'Contact', id: 'LIST' },
      ],
      // Optimistic remove — same reason as updateContact. Drops the row
      // from every cached list variant locally now; the background
      // refetch confirms it later. Rolls back on failure.
      onQueryStarted: async (id, { dispatch, getState, queryFulfilled }) => {
        const patches: { undo: () => void }[] = [];
        for (const originalArgs of collectCachedArgs(getState(), 'getContacts')) {
          patches.push(
            dispatch(
              contactsApi.util.updateQueryData(
                'getContacts',
                originalArgs as any,
                (draft) => {
                  const idx = draft.contacts.findIndex((c) => c.id === id);
                  if (idx === -1) return;
                  draft.contacts.splice(idx, 1);
                },
              ),
            ),
          );
        }
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p.undo());
        }
      },
    }),

    getContactContext: builder.query<ContactContextResponse, string>({
      query: (id) => `/contacts/${id}/context`,
      providesTags: (result, error, id) => [{ type: 'Contact', id: `context-${id}` }],
    }),
  }),
});

export const {
  useGetContactsQuery,
  useGetContactQuery,
  useGetContactContextQuery,
  useCreateContactMutation,
  useUpdateContactMutation,
  useDeleteContactMutation,
} = contactsApi;
